import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnDestroy,
  OnInit,
  output,
  ViewEncapsulation,
} from '@angular/core';

import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { inject } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  DialogHeaderComponent,
  DialogFooterComponent,
  ButtonComponent,
  FormFieldComponent,
  MessageComponent,
} from '@akostm/orbit-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { signal } from '@angular/core';
import {
  emailRegexpValidator,
  oneOfFieldsRequiredValidator,
  phoneNumberValidator,
} from '../../../../validators';
import { ErrorStore } from '../../../../stores';
import { ParticipantKind } from '@clientwire/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'shared-edit-participant-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DialogHeaderComponent,
    DialogFooterComponent,
    ButtonComponent,
    FormFieldComponent,
    TranslateModule,
    MessageComponent,
  ],
  templateUrl: './edit-participant-dialog.component.html',
  styleUrl: './edit-participant-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditParticipantDialogComponent implements OnDestroy, OnInit {
  readonly submitClicked = output<{
    displayName: string;
    phoneNumber: string;
    email: string;
    preferredLanguage: string | null;
  }>();
  readonly #errorStore = inject(ErrorStore);
  readonly #dialogRef = inject(DialogRef);
  readonly data = inject(DIALOG_DATA);
  readonly isSubmitting = signal(false);
  readonly editParticipantForm: FormGroup = new FormGroup({
    displayName: new FormControl<string | null>(null, [Validators.required]),
    phoneNumber: new FormControl<string | null>(null),
    email: new FormControl<string | null>(null),
    preferredLanguage: new FormControl<string | null>(null),
  });
  readonly #errorLocations = ['editParticipant'];
  readonly errors = computed(() => this.#errorStore.getErrorsByLocations(this.#errorLocations));
  readonly role = computed(() => this.data.participant.kind);
  readonly participantKindEnum = ParticipantKind;
  readonly #destroyRef = inject(DestroyRef);
  readonly #translateService = inject(TranslateService);

  readonly availableLanguages = [
    {
      label: this.#translateService.instant(
        'SHARED.CREATE_CONVERSATION_FORM.DEFAULT_TENANT_LANGUAGE'
      ),
      value: '',
    },
    {
      label: this.#translateService.instant('SHARED.CREATE_CONVERSATION_FORM.ENGLISH'),
      value: 'en',
    },
    {
      label: this.#translateService.instant('SHARED.CREATE_CONVERSATION_FORM.GERMAN'),
      value: 'de',
    },
  ];

  ngOnInit(): void {
    this.displayName.setValue(this.data.participant.displayName);
    this.preferredLanguage.setValue(this.data.participant.preferredLanguage ?? '');
    if (this.role() === ParticipantKind.Client) {
      // Add validators
      this.phoneNumber.setValidators([phoneNumberValidator()]);
      this.email.setValidators([emailRegexpValidator()]);
      this.editParticipantForm.addValidators([
        oneOfFieldsRequiredValidator(['phoneNumber', 'email']),
      ]);

      // Set values
      this.phoneNumber.setValue(this.data.participant.phoneNumber);
      this.email.setValue(this.data.participant.email);

      this.phoneNumber?.valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((value) => {
          if (this.displayName.pristine && value && !this.email.value) {
            this.displayName.setValue(value);
          }
        });

      this.email?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
        if (this.displayName.pristine && value) {
          this.displayName.setValue(value);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.#errorStore.clearErrorsByLocations(this.#errorLocations);
  }

  get displayName() {
    return this.editParticipantForm.get('displayName') as FormControl<string>;
  }

  get phoneNumber() {
    return this.editParticipantForm.get('phoneNumber') as FormControl<string>;
  }

  get email() {
    return this.editParticipantForm.get('email') as FormControl<string>;
  }

  get preferredLanguage() {
    return this.editParticipantForm.get('preferredLanguage') as FormControl<string>;
  }

  onCancel(): void {
    this.#dialogRef.close();
  }

  onCancelKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onCancel();
    }
  }

  onSubmit(): void {
    if (this.editParticipantForm.invalid) {
      this.editParticipantForm.markAllAsTouched();
      return;
    }
    this.isSubmitting.set(true);
    this.submitClicked.emit(this.editParticipantForm.value);
  }
}
